/**
 * Scope AI service - AI-powered project plan generation
 */
import apiClient from './api';

const scopeAIService = {
  /**
   * Check if AI generation is available
   * @returns {Promise<object>}
   */
  async getStatus() {
    const response = await apiClient.get('/scope-ai/status');
    return response.data;
  },

  /**
   * Analyze scope and get clarifying questions
   * @param {object} intake - Scope intake data
   * @returns {Promise<object>} - Questions and analysis summary
   */
  async analyzeScope(intake) {
    const response = await apiClient.post('/scope-ai/analyze', intake);
    return response.data;
  },

  /**
   * Generate a project plan from scope intake
   * @param {object} intake - Scope intake data
   * @returns {Promise<object>} - Generated plan
   */
  async generatePlan(intake) {
    const response = await apiClient.post('/scope-ai/generate', intake);
    return response.data;
  },

  /**
   * Apply a generated plan to a project
   * @param {string} projectId
   * @param {object} plan - The generated plan to apply
   * @returns {Promise<object>}
   */
  async applyPlan(projectId, plan) {
    const response = await apiClient.post(`/scope-ai/apply/${projectId}`, plan);
    return response.data;
  },

  /**
   * Get saved draft for a project
   * @param {string} projectId
   * @returns {Promise<object>}
   */
  async getDraft(projectId) {
    const response = await apiClient.get(`/scope-ai/draft/${projectId}`);
    return response.data;
  },

  /**
   * Save draft for a project
   * @param {string} projectId
   * @param {object} draftData - Form data and current step
   * @returns {Promise<object>}
   */
  async saveDraft(projectId, draftData) {
    const response = await apiClient.put(`/scope-ai/draft/${projectId}`, draftData);
    return response.data;
  },

  /**
   * Clear draft after plan is applied
   * @param {string} projectId
   * @returns {Promise<object>}
   */
  async clearDraft(projectId) {
    const response = await apiClient.delete(`/scope-ai/draft/${projectId}`);
    return response.data;
  },

  // ============== FEEDBACK METHODS ==============

  /**
   * Get all feedback for a project's plan
   * @param {string} projectId
   * @returns {Promise<object>}
   */
  async getFeedback(projectId) {
    const response = await apiClient.get(`/scope-ai/feedback/${projectId}`);
    return response.data;
  },

  /**
   * Add feedback to a plan item
   * @param {string} projectId
   * @param {object} feedback - { item_type, item_name, feedback_type, feedback_text, suggested_value }
   * @returns {Promise<object>}
   */
  async addFeedback(projectId, feedback) {
    const response = await apiClient.post(`/scope-ai/feedback/${projectId}`, { feedback });
    return response.data;
  },

  /**
   * Remove specific feedback
   * @param {string} projectId
   * @param {string} feedbackId
   * @returns {Promise<object>}
   */
  async removeFeedback(projectId, feedbackId) {
    const response = await apiClient.delete(`/scope-ai/feedback/${projectId}/${feedbackId}`);
    return response.data;
  },

  /**
   * Clear all feedback (after regeneration)
   * @param {string} projectId
   * @returns {Promise<object>}
   */
  async clearAllFeedback(projectId) {
    const response = await apiClient.delete(`/scope-ai/feedback/${projectId}`);
    return response.data;
  },

  /**
   * Refine plan based on feedback (DEPRECATED - regenerates everything)
   * @param {string} projectId
   * @returns {Promise<object>}
   */
  async refinePlan(projectId, { confirm = false } = {}) {
    // `confirm` is a QUERY parameter on the backend
    // (backend/routes/scope_ai.py::refine_plan_with_feedback, `confirm: bool = False`),
    // and it defaults to FALSE here on purpose. The first call must be able to
    // reach the 428, because that refusal is what makes the confirmation
    // deliberate. A default of true would make the gate unreachable from the
    // UI while leaving it in the backend, which is worse than not having it:
    // the server would look governed and the product would not be.
    const response = await apiClient.post(
      `/scope-ai/refine/${projectId}`,
      null,
      confirm ? { params: { confirm: true } } : undefined,
    );
    return response.data;
  },

  /**
   * Refine ONLY items with feedback - targeted updates
   * Does not regenerate the entire plan
   * @param {string} projectId
   * @returns {Promise<object>}
   */
  async refineItems(projectId, { confirm = false } = {}) {
    // Same contract, same default, same reason. backend/routes/scope_ai.py
    // raises the identical 428 on this route.
    const response = await apiClient.post(
      `/scope-ai/refine-item/${projectId}`,
      null,
      confirm ? { params: { confirm: true } } : undefined,
    );
    return response.data;
  },
};

export default scopeAIService;
